/**
 * Universal Builder governance — persisted alongside pool lifecycle retention
 * in the tenant ``WorkspaceSettings`` row (REST ``PUT /tenants/{tenantId}/settings``).
 *
 * Used from **Workspace settings** (admin route) and the workbench **Tenant** slide-over.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  getWorkspaceSettings,
  putWorkspaceSettings,
} from '../../services/poolLifecycleApi.js';

function numOr(defaultVal, raw) {
  if (raw === '' || raw === null || raw === undefined) return defaultVal;
  const n = Number(raw);
  return Number.isFinite(n) ? n : defaultVal;
}

export default function TenantBuilderLimitsPanel({ tenantId }) {
  const tid = String(tenantId || '').trim();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savedHint, setSavedHint] = useState(null);

  const [workspaceSoftCapGb, setWorkspaceSoftCapGb] = useState('');
  const [builderCodebuildTimeoutMinutes, setBuilderCodebuildTimeoutMinutes] =
    useState('');
  const [builderMaxConcurrentRuns, setBuilderMaxConcurrentRuns] = useState('');
  const [builderMonthlyBedrockUsdCap, setBuilderMonthlyBedrockUsdCap] =
    useState('');

  const hydrateFromApi = useCallback((s) => {
    setWorkspaceSoftCapGb(String(s?.builderWorkspaceSoftCapGb ?? 5));
    setBuilderCodebuildTimeoutMinutes(String(s?.builderCodebuildTimeoutMinutes ?? 30));
    setBuilderMaxConcurrentRuns(String(s?.builderMaxConcurrentRuns ?? 3));
    setBuilderMonthlyBedrockUsdCap(String(s?.builderMonthlyBedrockUsdCap ?? 0));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setErr(null);
      setSavedHint(null);
      if (!tid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getWorkspaceSettings(tid);
        const s = res.settings;
        if (!cancelled && s) hydrateFromApi(s);
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tid, hydrateFromApi]);

  const onSave = async () => {
    setErr(null);
    setSavedHint(null);
    if (!tid) {
      setErr('No tenant selected.');
      return;
    }
    const soft = Math.trunc(numOr(5, workspaceSoftCapGb));
    const tmo = Math.trunc(numOr(30, builderCodebuildTimeoutMinutes));
    const conc = Math.trunc(numOr(3, builderMaxConcurrentRuns));
    let usd = numOr(0, builderMonthlyBedrockUsdCap);
    if (soft < 0 || soft > 1_000_000) {
      setErr('Workspace soft cap must be between 0 (unlimited) and 1000000 GiB.');
      return;
    }
    if (tmo < 5 || tmo > 480) {
      setErr('CodeBuild shell timeout must be between 5 and 480 minutes.');
      return;
    }
    if (conc < 1 || conc > 100) {
      setErr('Max concurrent builder runs must be between 1 and 100.');
      return;
    }
    if (usd < 0 || usd > 999_999.99) {
      setErr('Monthly Bedrock USD cap must be between 0 and 999999.99.');
      return;
    }
    usd = Math.round(usd * 100) / 100;
    setLoading(true);
    try {
      const res = await putWorkspaceSettings(tid, {
        builderWorkspaceSoftCapGb: soft,
        builderCodebuildTimeoutMinutes: tmo,
        builderMaxConcurrentRuns: conc,
        builderMonthlyBedrockUsdCap: usd,
      });
      if (res?.settings) hydrateFromApi(res.settings);
      setSavedHint('Saved Universal Builder limits.');
    } catch (e) {
      setErr(e?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (!tid) {
    return (
      <div className="p-5 text-[13px] text-slate-500">
        Sign in with a tenant that carries an explicit workspace id (
        <code className="text-slate-700">tenant_id</code>) to configure builder
        guardrails.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-auto p-5">
      <p className="text-[13px] leading-snug text-slate-600">
        These limits gate the Universal Builder (CodeBuild) path before spend is
        incurred. Monthly Bedrock caps use metering rollups keyed by billing org;
        tenants without{' '}
        <code className="text-[11px] text-slate-800">OrgId</code> on WORKSPACE
        META skip that check until onboarding sets it up.
      </p>

      {err ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
          {err}
        </div>
      ) : null}

      {savedHint ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900">
          {savedHint}
        </div>
      ) : null}

      <div className="grid max-w-lg grid-cols-1 gap-3 text-[13px]">
        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">
            Builder workspace soft cap (GiB){' '}
            <span className="font-normal text-slate-400">— 0 = unlimited</span>
          </span>
          <input
            type="number"
            min={0}
            className="rounded border border-slate-200 px-2 py-1 font-mono text-[12px] text-slate-900 outline-none ring-blue-400 focus:ring-1"
            value={workspaceSoftCapGb}
            disabled={loading}
            onChange={(e) => setWorkspaceSoftCapGb(e.target.value)}
          />
          <span className="text-[11px] text-slate-500">
            Applies to aggregated object size under{' '}
            <code className="text-[10px]">runs/&lt;tid&gt;/</code> in the builder bucket.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">
            CodeBuild wall timeout (minutes)
          </span>
          <input
            type="number"
            min={5}
            max={480}
            className="rounded border border-slate-200 px-2 py-1 font-mono text-[12px] text-slate-900 outline-none ring-blue-400 focus:ring-1"
            value={builderCodebuildTimeoutMinutes}
            disabled={loading}
            onChange={(e) => setBuilderCodebuildTimeoutMinutes(e.target.value)}
          />
          <span className="text-[11px] text-slate-500">
            Enforced in the runner via <code>SIGALRM</code> (POSIX). Must stay
            under the account CodeBuild project ceiling.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">
            Max concurrent builder runs (
            <code className="text-[11px]">IN_PROGRESS</code> per tenant)
          </span>
          <input
            type="number"
            min={1}
            max={100}
            className="rounded border border-slate-200 px-2 py-1 font-mono text-[12px] text-slate-900 outline-none ring-blue-400 focus:ring-1"
            value={builderMaxConcurrentRuns}
            disabled={loading}
            onChange={(e) => setBuilderMaxConcurrentRuns(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="font-medium text-slate-700">
            Estimated Bedrock spend cap (USD / calendar month){' '}
            <span className="font-normal text-slate-400">— 0 = off</span>
          </span>
          <input
            type="number"
            min={0}
            step={0.01}
            max={999_999.99}
            className="rounded border border-slate-200 px-2 py-1 font-mono text-[12px] text-slate-900 outline-none ring-blue-400 focus:ring-1"
            value={builderMonthlyBedrockUsdCap}
            disabled={loading}
            onChange={(e) => setBuilderMonthlyBedrockUsdCap(e.target.value)}
          />
          <span className="text-[11px] text-slate-500">
            Mirrors the metering table via{' '}
            <code className="text-[10px]">OrgBillingIndex</code>; estimated model
            cost only.
          </span>
        </label>
      </div>

      <div className="mt-2">
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Working…' : 'Save Universal Builder limits'}
        </button>
      </div>
    </div>
  );
}
